<script lang="ts">
  let props = $props()
  let { answer } = props
</script>

<div
  class="lg:ml-8 mt-2 border-l-2 border-dashed border-blue-800 px-4 lg:px-6 py-3 lg:py-6 bg-sky-50 rounded-r"
>
  {#if answer == 1}
    <p class="text-base lg:text-xl/8 text-gray-700">
      A: <span class="font-bold"
        >“They talk it through, even if it’s messy - your team knows how to
        navigate real tension.”</span
      >
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700">
      This shows <span class="font-bold">mature team trust</span> and high psychological
      safety.
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700">
      People are confident enough to stay in the tension - not avoid it - and
      care enough to resolve it.
    </p>
    <p
      class="mt-2 text-xs lg:text-base/8 text-gray-700 px-0 lg:px-10 italic font-semibold"
    >
      In Fast Attack teams, conflict isn’t a threat - it’s a tool. Crews that
      operate under pressure must be able to challenge each other in real time
      without damaging the mission or the team.
    </p>

    <p class="mt-3 text-base lg:mt-4 lg:text-lg/8 text-gray-700 font-bold">
      Level-Up Opportunity
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4">
      Ask: <span class="italic"
        >Are we reinforcing the behavior behind this strength?</span
      >
    </p>
    <p
      class="mt-1 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4 font-semibold"
    >
      Capture how your team handled a tough moment well - then turn it into a
      model for others. Trust grows when courageous conversations are
      celebrated, not just tolerated.
    </p>
  {:else if answer == 2}
    <p class="text-base lg:text-xl/8 text-gray-700">
      B: <span class="font-bold"
        >“One of them backs down to avoid making it uncomfortable.”
      </span>
    </p>
    <p class="mt-4 text-sm lg:text-base/8 text-gray-700">
      This points to <span class="font-bold"
        >surface-level harmony masking underlying avoidance.</span
      >
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700">
      Team members may respect each other, but they haven’t built the muscle to
      disagree productively - which means ideas are filtered, not fully
      explored.
    </p>

    <p
      class="mt-2 text-xs lg:text-base/8 text-gray-700 px-0 lg:px-10 italic font-semibold"
    >
      On a submarine, avoiding the hard conversation doesn’t protect the team -
      it endangers the mission. Silence can be deadly when pressure builds.
    </p>

    <p class="mt-3 text-base lg:text-lg/8 text-gray-700 font-bold">
      Level-Up Opportunity
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4">
      Start building “productive tension” skills into your team rhythm. Practice
      disagreeing in low-stakes scenarios and model how to stay in the
      conversation.
    </p>
    <p
      class="mt-1 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4 font-semibold"
    >
      Ask: <span class="italic"
        >Where is politeness getting in the way of progress?</span
      >
    </p>
  {:else if answer == 3}
    <p class="text-base lg:text-xl/8 text-gray-700">
      C: <span class="font-bold"
        >“The conversation derails and you have to step in to manage it.”</span
      >
    </p>
    <p class="mt-4 text-sm lg:text-base/8 text-gray-700">
      This signals that <span class="font-bold"
        >trust has not matured to peer-level ownership.</span
      >
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700">
      Team members look to you as the emotional regulator - which keeps you in
      the center and robs the team of the chance to lead through conflict.
    </p>
    <p
      class="mt-2 text-xs lg:text-base/8 text-gray-700 px-0 lg:px-10 italic font-semibold"
    >
      This is like the captain needing to calm every crew disagreement - it's
      not sustainable, and it keeps the team dependent and reactive.
    </p>

    <p class="mt-3 text-base lg:text-lg/8 text-gray-700 font-bold">
      Level-Up Opportunity
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4">
      After the next conflict, don’t just resolve it - debrief it. Ask: <span
        class="italic"
        >What happened, what did we avoid, and how do we want to handle it
        differently next time?
      </span>
    </p>
    <p
      class="mt-1 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4 font-semibold"
    >
      Teach the crew to manage pressure without waiting for the officer to enter
      the room.
    </p>
  {:else if answer == 4}
    <p class="text-base lg:text-xl/8 text-gray-700">
      D: <span class="font-bold"
        >“Everyone gets quiet and moves on - you can feel the tension lingering
        under the surface.”</span
      >
    </p>
    <p class="mt-4 text-sm lg:text-base/8 text-gray-700">
      This is a critical signal of low psychological safety.
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700">
      People are self-protecting - not team-protecting. The lack of open
      conflict isn’t a strength - it’s a shutdown.
    </p>
    <p
      class="mt-2 text-xs lg:text-base/8 text-gray-700 px-0 lg:px-10 italic font-semibold"
    >
      In Fast Attack terms, this is like shutting down an alarm because it's
      loud - instead of addressing the actual emergency.
    </p>

    <p class="mt-3 text-base lg:text-lg/8 text-gray-700 font-bold">
      Level-Up Opportunity
    </p>
    <p class="mt-2 text-base/8 text-gray-700 ml-0 lg:ml-4">
      You need to re-establish emotional safety. Acknowledge the silence, and
      invite candor.
    </p>
    <p
      class="mt-1 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4 font-semibold"
    >
      Ask: <span class="italic"
        >What are we not saying that needs to be said?</span
      >
    </p>
    <p class="mt-2 text-sm lg:text-base/8 text-gray-700 ml-0 lg:ml-4">
      Create spaces where speaking up is the norm - not the exception.
    </p>
  {/if}
</div>
